import {
    IAttendeeCountResponse,
    IParticipationResponse,
    IParticipationSummary,
} from "@Interfaces";
import { Http } from "@Services/API/Http";
import { toParticipationSummary } from "./mapper";

export { ParticipationUsageService } from "./usage";

interface IApiEnvelope<T> {
    success: boolean;
    data: T;
}

/** Status and backend `message` of a failed participation request. */
export class ParticipationApiError extends Error {
    status: number;

    constructor(status: number, message: string) {
        super(message);
        this.status = status;
    }
}

// The API error handler serves JSON as text/html, so the body may still be a string.
const toParticipationApiError = (error: any): ParticipationApiError => {
    const status = error?.response?.status ?? 0;
    let body = error?.response?.data;
    if (typeof body === "string") {
        try {
            body = JSON.parse(body);
        } catch {
            body = null;
        }
    }
    return new ParticipationApiError(status, body?.message ?? "");
};

export const ParticipationService = {
    getSummary: async (channelId: string): Promise<IParticipationSummary> => {
        try {
            const response = await Http.Request<
                IApiEnvelope<IParticipationResponse>
            >({
                method: "GET",
                url: `/groups/${channelId}/participation`,
            });
            return toParticipationSummary(response.data);
        } catch (error) {
            throw toParticipationApiError(error);
        }
    },

    setAttendeeCount: async (
        channelId: string,
        count: number
    ): Promise<IAttendeeCountResponse> => {
        try {
            const response = await Http.Request<
                IApiEnvelope<IAttendeeCountResponse>
            >({
                method: "PUT",
                url: `/groups/${channelId}/attendee-count`,
                data: { attendee_count: count },
            });
            return response.data;
        } catch (error) {
            throw toParticipationApiError(error);
        }
    },
};
